import {MvsCoreService} from "../../../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../../../core/shared/service/crud/mvs-crud.service";
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {EiFieldRuleDto} from '../../../../model/dto/entity/ei-field-rule.dto';
import {EiFieldRecencyAction} from '../../../../enum/ei-field-recency-action.enum';
import {EiImportTypeDto} from '../../../../model/dto/entity/ei-import-type.dto';
import {EiStructureDto} from '../../../../model/dto/entity/ei-structure.dto';
import {EiStructureFieldDto} from '../../../../model/dto/entity/ei-structure-field.dto';
import {ObjectRequestList} from "../../../../../../core/shared/dto/object/request/object-request-list";
import {FilterCriteria} from "../../../../../../core/shared/filter/api/filter.criteria";

@Component({
    selector: 'ei-rule-config',
    templateUrl: './rule-config.component.html',
    styleUrls: ['./rule-config.component.scss'],
    standalone: false
})
export class RuleConfigComponent implements OnInit, OnChanges {

    fieldRuleService: MvsCrudService;
    @Input() importType: EiImportTypeDto;
    @Input() structures: EiStructureDto[] = [];
    @Input() structureFields: EiStructureFieldDto[] = [];
    @Output() ruleChanged = new EventEmitter<any>();

    rules: EiFieldRuleDto[] = [];
    selectedRule: EiFieldRuleDto;
    showRuleDialog: boolean = false;
    editMode: boolean = false;

    // New rule form
    newRule: Partial<EiFieldRuleDto> = {};

    // Dropdown options
    actionOptions = [
        { label: 'Import bevorzugen', value: EiFieldRecencyAction.PREFER_IMPORT },
        { label: 'System behalten', value: EiFieldRecencyAction.PREFER_SYSTEM },
        { label: 'Zeitstempel vergleichen', value: EiFieldRecencyAction.USE_TIMESTAMP },
        { label: 'Logik verwenden', value: EiFieldRecencyAction.USE_LOGIC },
        { label: 'Manuelle Entscheidung', value: EiFieldRecencyAction.MANUAL_DECISION },
        { label: 'Ablehnen', value: EiFieldRecencyAction.REJECT }
    ];

    RecencyAction = EiFieldRecencyAction;

    constructor(protected coreService: MvsCoreService) {
        this.fieldRuleService = this.coreService.getCrudService("ei.EiFieldRule");

    }

    ngOnInit(): void {
        if (this.importType) {
            this.loadRules();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['importType'] && this.importType) {
            this.loadRules();
        }
    }

    loadRules(): void {
        if (!this.importType?.id) {
            return;
        }

        const request = new ObjectRequestList(false, [
            FilterCriteria.create('importType', FilterCriteria.cOperatorEqual, this.importType.id),
        ],[]);

        this.fieldRuleService.list(request).subscribe({
            next: (result) => {
                this.rules = result.entries.sort((a, b) => (b.priority || 0) - (a.priority || 0));
            },
            error: (err) => {
                console.error('Error loading rules', err);
            }
        });
    }

    openNewRuleDialog(): void {
        this.editMode = false;
        this.newRule = {
            importTypeDtoId: this.importType.id,
            active: true,
            priority: this.rules.length + 1,
            action: EiFieldRecencyAction.USE_TIMESTAMP
        };
        this.showRuleDialog = true;
    }

    editRule(rule: EiFieldRuleDto): void {
        this.editMode = true;
        this.selectedRule = rule;
        this.newRule = { ...rule };
        this.showRuleDialog = true;
    }

    saveRule(): void {
        if (this.editMode) {
            this.fieldRuleService.update(<EiFieldRuleDto>this.newRule).subscribe({
                next: (result) => {
                    this.loadRules();
                    this.showRuleDialog = false;
                    this.ruleChanged.emit(result);
                },
                error: (err) => {
                    console.error('Error updating rule', err);
                }
            });
        } else {
            this.fieldRuleService.create(<EiFieldRuleDto>this.newRule).subscribe({
                next: (result) => {
                    this.loadRules();
                    this.showRuleDialog = false;
                    this.ruleChanged.emit(result);
                },
                error: (err) => {
                    console.error('Error creating rule', err);
                }
            });
        }
    }

    deleteRule(rule: EiFieldRuleDto): void {
        if (confirm('Regel wirklich löschen?')) {
            this.fieldRuleService.delete(rule.id).subscribe({
                next: () => {
                    this.loadRules();
                },
                error: (err) => {
                    console.error('Error deleting rule', err);
                }
            });
        }
    }

    toggleRuleActive(rule: EiFieldRuleDto): void {
        rule.active = !rule.active;
        this.fieldRuleService.update(rule).subscribe({
            next: () => {
                this.loadRules();
            },
            error: (err) => {
                console.error('Error updating rule', err);
                rule.active = !rule.active; // Revert
            }
        });
    }

    cancelDialog(): void {
        this.showRuleDialog = false;
        this.selectedRule = null;
        this.newRule = {};
    }

    getActionLabel(action: EiFieldRecencyAction): string {
        const option = this.actionOptions.find(o => o.value === action);
        return option?.label || 'Unbekannt';
    }

    getActionIcon(action: EiFieldRecencyAction): string {
        switch (action) {
            case EiFieldRecencyAction.PREFER_IMPORT:
                return 'fa-solid fa-download text-blue-500';
            case EiFieldRecencyAction.PREFER_SYSTEM:
                return 'fa-solid fa-database text-green-500';
            case EiFieldRecencyAction.USE_TIMESTAMP:
                return 'fa-solid fa-clock text-purple-500';
            case EiFieldRecencyAction.USE_LOGIC:
                return 'fa-solid fa-code-branch text-teal-500';
            case EiFieldRecencyAction.MANUAL_DECISION:
                return 'fa-solid fa-user-check text-orange-500';
            case EiFieldRecencyAction.REJECT:
                return 'fa-solid fa-ban text-red-500';
            default:
                return 'fa-solid fa-question text-gray-500';
        }
    }

    getScopeLabel(rule: EiFieldRuleDto): string {
        if (rule.structureFieldDtoId) {
            return 'Feld: ' + (rule.structureFieldDtoName || rule.structureFieldDtoId);
        }
        if (rule.structureDtoId) {
            return 'Struktur: ' + (rule.structureDtoName || rule.structureDtoId);
        }
        return 'Import Type';
    }

    getScopeSeverity(rule: EiFieldRuleDto) {
        if (rule.structureFieldDtoId) {
            return 'success'; // Field-specific
        }
        if (rule.structureDtoId) {
            return 'info'; // Structure-specific
        }
        return 'secondary'; // ImportType-level
    }

}
